import React, { useState, useEffect } from 'react';
import './Loader.css';
import { TableSkeleton } from '../TableSkeleton/TableSkeleton';

export interface LoaderProps {
  /** Размер лоадера */
  size?: 'small' | 'medium' | 'large';
  /** Цвет лоадера */
  color?: 'primary' | 'secondary' | 'white' | 'success' | 'error';
  /** Тип лоадера */
  variant?: 'spinner' | 'dots' | 'pulse' | 'skeleton';
  /** Текст для отображения под лоадером */
  text?: string;
  /** Показывать ли текст */
  showText?: boolean;
  /** Дополнительные CSS классы */
  className?: string;
  /** Стили для контейнера */
  style?: React.CSSProperties;
  /** Полноэкранный режим */
  fullscreen?: boolean;
  /** Прозрачность фона в полноэкранном режиме */
  overlayOpacity?: number;
  /** Показывать ли лоадер */
  visible?: boolean;
  /** Задержка скрытия в миллисекундах */
  hideDelay?: number;
}

const Loader: React.FC<LoaderProps> = ({
  size = 'medium',
  color = 'primary',
  variant = 'spinner',
  text,
  showText = false,
  className = '',
  style,
  fullscreen = false,
  overlayOpacity = 0.5,
  visible = true,
  hideDelay = 500,
}) => {
  const [isVisible, setIsVisible] = useState(visible);

  useEffect(() => {
    if (visible) {
      setIsVisible(true);
    } else {
      // Задержка скрытия лоадера
      const timer = setTimeout(() => {
        setIsVisible(false);
      }, hideDelay);
      
      return () => clearTimeout(timer);
    }
  }, [visible, hideDelay]);

  // Если лоадер не видим, не рендерим его
  if (!isVisible) {
    return null;
  }

  const renderLoader = () => {
    switch (variant) {
      case 'spinner':
        return (
          <div className={`loader-spinner loader-${size} loader-${color}`}>
            <div className="spinner-ring"></div>
          </div>
        );
      
      case 'dots':
        return (
          <div className={`loader-dots loader-${size} loader-${color}`}>
            <div className="dot"></div>
            <div className="dot"></div>
            <div className="dot"></div>
          </div>
        );
      
      case 'pulse':
        return (
          <div className={`loader-pulse loader-${size} loader-${color}`}>
            <div className="pulse-circle"></div>
          </div>
        );
      
      case 'skeleton':
        return (
          <TableSkeleton 
            rows={size === 'small' ? 5 : size === 'medium' ? 8 : 10}
            columns={3}
            showHeader={true}
            maxHeight="68vh"
            minHeight="400px"
          />
        );
      
      default:
        return (
          <div className={`loader-spinner loader-${size} loader-${color}`}>
            <div className="spinner-ring"></div>
          </div>
        );
    }
  };

  const loaderContent = (
    <div 
      className={`loader-container ${className}`}
      style={style}
    >
      {renderLoader()}
      {showText && text && (
        <div className={`loader-text loader-text-${color}`}>
          {text}
        </div>
      )}
    </div>
  );

  if (fullscreen) {
    return (
      <div 
        className={`loader-fullscreen ${className}`}
        style={{ 
          backgroundColor: `rgba(0, 0, 0, ${overlayOpacity})`,
          ...style 
        }}
      >
        {loaderContent}
      </div>
    );
  }

  return loaderContent;
};

export default Loader; 